import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Checkbox,
  ListItemText,
  alpha,
  useTheme,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Plus, Trash2, Edit2, Clock, Mail, ArrowLeft, GitBranch } from "lucide-react";
import {
  fetchMarketingSequence,
  updateMarketingSequence,
  createMarketingSequenceStep,
  updateMarketingSequenceStep,
  deleteMarketingSequenceStep,
  fetchMarketingTemplates,
  fetchMarketingSegments,
} from "../../../../../../redux/actions/marketingAction";
import DeleteConfirmDialog from "../DeleteConfirmDialog";

const SequenceDetail = ({ sequenceId }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { templates, segments } = useSelector((state) => state.marketing);

  const [sequence, setSequence] = useState(null);
  const [loadingSeq, setLoadingSeq] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({ name: "", segment_ids: [] });
  const [stepFormOpen, setStepFormOpen] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteStepId, setDeleteStepId] = useState(null);
  const [stepForm, setStepForm] = useState({
    step_order: 0, delay_days: 0, send_time: "", template_id: "", subject: "", subject_b: "",
    ab_test_enabled: false, ab_test_sample_pct: 10, ab_test_winner_wait_hours: 4,
  });

  const loadSequence = useCallback(async () => {
    setLoadingSeq(true);
    try {
      const data = await dispatch(fetchMarketingSequence(sequenceId));
      setSequence(data);
    } finally {
      setLoadingSeq(false);
    }
  }, [dispatch, sequenceId]);

  useEffect(() => {
    loadSequence();
    dispatch(fetchMarketingTemplates());
    dispatch(fetchMarketingSegments());
  }, [loadSequence, dispatch]);

  // Edit sequence
  const isEditAllSelected = editFormData.segment_ids.length === 0;
  const editFormValid = editFormData.name.trim();

  const handleOpenEdit = () => {
    setEditFormData({
      name: sequence?.name || "",
      segment_ids: (sequence?.segments || []).map((s) => s.id),
    });
    setEditOpen(true);
  };

  const handleEditSegmentChange = (e) => {
    const val = e.target.value;
    if (val.includes("__all__")) {
      setEditFormData({ ...editFormData, segment_ids: [] });
      return;
    }
    setEditFormData({ ...editFormData, segment_ids: typeof val === "string" ? val.split(",") : val });
  };

  const renderSegmentValue = (selectedIds, isAll) => {
    if (isAll) return t("mkt_all_contacts");
    return selectedIds
      .map((id) => (segments || []).find((s) => s.id === id)?.name || id)
      .join(", ");
  };

  const handleUpdateSequence = async () => {
    if (!editFormValid) return;
    setSubmitting(true);
    try {
      await dispatch(updateMarketingSequence(sequenceId, {
        name: editFormData.name,
        segment_ids: editFormData.segment_ids,
      }));
      setEditOpen(false);
      loadSequence();
    } finally {
      setSubmitting(false);
    }
  };

  // Steps
  const handleOpenStepForm = (step = null) => {
    setEditingStep(step);
    setStepForm(step ? {
      step_order: step.step_order, delay_days: step.delay_days,
      send_time: step.send_time || "",
      template_id: step.template_id || "", subject: step.subject, subject_b: step.subject_b || "",
      ab_test_enabled: step.ab_test_enabled, ab_test_sample_pct: step.ab_test_sample_pct,
      ab_test_winner_wait_hours: step.ab_test_winner_wait_hours,
    } : {
      step_order: (sequence?.steps || []).length, delay_days: 0,
      send_time: "09:00",
      template_id: "", subject: "", subject_b: "",
      ab_test_enabled: false, ab_test_sample_pct: 10, ab_test_winner_wait_hours: 4,
    });
    setStepFormOpen(true);
  };

  const handleSaveStep = async () => {
    setSubmitting(true);
    try {
      if (editingStep) {
        await dispatch(updateMarketingSequenceStep(sequenceId, editingStep.id, stepForm));
      } else {
        await dispatch(createMarketingSequenceStep(sequenceId, stepForm));
      }
      setStepFormOpen(false);
      loadSequence();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteStep = (stepId) => {
    setDeleteStepId(stepId);
  };

  const confirmDeleteStep = async () => {
    await dispatch(deleteMarketingSequenceStep(sequenceId, deleteStepId));
    setDeleteStepId(null);
    loadSequence();
  };

  if (loadingSeq) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!sequence) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Sequence not found
        </Typography>
      </Box>
    );
  }

  const steps = sequence.steps || [];
  const seqSegments = sequence.segments || [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <IconButton size="small" onClick={() => navigate("/admin/marketing/sequences")}>
          <ArrowLeft size={18} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
            {sequence.name || t("mkt_default_sequence")}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
            {seqSegments.length > 0 ? (
              seqSegments.map((seg) => (
                <Chip
                  key={seg.id}
                  label={seg.name}
                  size="small"
                  sx={{
                    borderRadius: "6px",
                    fontSize: "0.65rem",
                    height: 20,
                    bgcolor: seg.color ? alpha(seg.color, 0.1) : alpha(theme.palette.primary.main, 0.1),
                    color: seg.color || theme.palette.primary.main,
                  }}
                />
              ))
            ) : (
              <Chip
                label={t("mkt_all_contacts")}
                size="small"
                sx={{ borderRadius: "6px", fontSize: "0.65rem", height: 20 }}
              />
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Edit2 size={14} />}
            onClick={handleOpenEdit}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
          >
            {t("mkt_edit_sequence")}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<Plus size={14} />}
            onClick={() => handleOpenStepForm()}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
          >
            {t("mkt_add_step")}
          </Button>
        </Box>
      </Box>

      {/* Steps Timeline */}
      {steps.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, borderRadius: "12px", border: `1px solid ${theme.palette.divider}`, textAlign: "center" }}>
          <GitBranch size={32} color={theme.palette.text.secondary} />
          <Typography sx={{ color: theme.palette.text.secondary, mt: 2 }}>
            {t("mkt_no_steps")}
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ p: 3, borderRadius: "12px", border: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ pl: 2 }}>
            {steps.map((step, idx) => (
              <Box key={step.id}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: "8px",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    }}>
                      <Mail size={16} color={theme.palette.primary.main} />
                    </Box>
                    {idx < steps.length - 1 && (
                      <Box sx={{ width: 2, height: 32, bgcolor: alpha(theme.palette.divider, 0.5), my: 0.5 }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, pb: idx < steps.length - 1 ? 2 : 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{step.subject}</Typography>
                        <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                          <Chip
                            icon={<Clock size={10} />}
                            label={`J+${step.delay_days}${step.send_time ? ` ${t("mkt_at")} ${step.send_time}` : ""}`}
                            size="small"
                            sx={{ borderRadius: "6px", fontSize: "0.65rem", height: 20 }}
                          />
                          {step.ab_test_enabled && (
                            <Chip label={t("mkt_ab_test")} size="small" color="info" sx={{ borderRadius: "6px", fontSize: "0.65rem", height: 20 }} />
                          )}
                          {step.ab_test_winner && (
                            <Chip label={`${t("mkt_winner")}${step.ab_test_winner.toUpperCase()}`} size="small" color="success" sx={{ borderRadius: "6px", fontSize: "0.65rem", height: 20 }} />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenStepForm(step)}>
                            <Edit2 size={14} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDeleteStep(step.id)}>
                            <Trash2 size={14} color={theme.palette.error?.main} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Step Form Dialog */}
      <Dialog open={stepFormOpen} onClose={() => setStepFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{editingStep ? t("mkt_edit_step") : t("mkt_add_step")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label={t("mkt_step_order")} type="number" value={stepForm.step_order} onChange={(e) => setStepForm({ ...stepForm, step_order: parseInt(e.target.value) })} size="small" sx={{ width: 120 }} />
              <TextField label={t("mkt_delay_days")} type="number" value={stepForm.delay_days} onChange={(e) => setStepForm({ ...stepForm, delay_days: parseInt(e.target.value) })} size="small" sx={{ width: 140 }} />
              <TextField
                label={t("mkt_send_time")}
                value={stepForm.send_time}
                onChange={(e) => {
                  let v = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
                  if (v.length >= 3) v = v.slice(0, 2) + ":" + v.slice(2);
                  setStepForm({ ...stepForm, send_time: v });
                }}
                onBlur={() => {
                  const match = stepForm.send_time.match(/^(\d{1,2}):?(\d{2})$/);
                  if (match) {
                    const h = Math.min(23, parseInt(match[1]));
                    const m = Math.min(59, parseInt(match[2]));
                    setStepForm({ ...stepForm, send_time: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}` });
                  } else if (stepForm.send_time) {
                    setStepForm({ ...stepForm, send_time: "" });
                  }
                }}
                placeholder={t("mkt_hhmm")}
                size="small"
                sx={{ width: 160 }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 5 }}
                helperText={stepForm.send_time ? "" : t("mkt_send_asap")}
              />
            </Box>
            <TextField label={t("mkt_subject_a")} value={stepForm.subject} onChange={(e) => setStepForm({ ...stepForm, subject: e.target.value })} fullWidth size="small" required />
            <FormControl size="small" fullWidth>
              <InputLabel>{t("mkt_email_template")}</InputLabel>
              <Select value={stepForm.template_id} onChange={(e) => setStepForm({ ...stepForm, template_id: e.target.value })} label={t("mkt_email_template")}>
                <MenuItem value="">{t("mkt_none")}</MenuItem>
                {(templates || []).map((tpl) => <MenuItem key={tpl.id} value={tpl.id}>{tpl.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={stepForm.ab_test_enabled} onChange={(e) => setStepForm({ ...stepForm, ab_test_enabled: e.target.checked })} />}
              label={t("mkt_enable_ab")}
            />
            {stepForm.ab_test_enabled && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2, bgcolor: alpha(theme.palette.divider, 0.2), borderRadius: "8px" }}>
                <TextField label={t("mkt_subject_b")} value={stepForm.subject_b} onChange={(e) => setStepForm({ ...stepForm, subject_b: e.target.value })} fullWidth size="small" />
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField label={t("mkt_sample_pct")} type="number" value={stepForm.ab_test_sample_pct} onChange={(e) => setStepForm({ ...stepForm, ab_test_sample_pct: parseInt(e.target.value) })} size="small" sx={{ width: 120 }} />
                  <TextField label={t("mkt_winner_wait")} type="number" value={stepForm.ab_test_winner_wait_hours} onChange={(e) => setStepForm({ ...stepForm, ab_test_winner_wait_hours: parseInt(e.target.value) })} size="small" sx={{ width: 160 }} />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStepFormOpen(false)}>{t("common_cancel")}</Button>
          <Button variant="contained" onClick={handleSaveStep} disabled={!stepForm.subject || !stepForm.template_id || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
            {submitting ? t("mkt_saving") : t("common_save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Sequence Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{t("mkt_edit_sequence")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label={t("mkt_sequence_name")}
              value={editFormData.name}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              fullWidth
              size="small"
              required
            />
            <FormControl size="small" fullWidth>
              <InputLabel>{t("mkt_target_segments")}</InputLabel>
              <Select
                multiple
                value={isEditAllSelected ? ["__all__"] : editFormData.segment_ids}
                onChange={handleEditSegmentChange}
                label={t("mkt_target_segments")}
                renderValue={() => renderSegmentValue(editFormData.segment_ids, isEditAllSelected)}
              >
                <MenuItem value="__all__">
                  <Checkbox checked={isEditAllSelected} size="small" />
                  <ListItemText primary={t("mkt_all_contacts")} />
                </MenuItem>
                {(segments || []).map((seg) => (
                  <MenuItem key={seg.id} value={seg.id}>
                    <Checkbox checked={editFormData.segment_ids.indexOf(seg.id) > -1} size="small" />
                    <ListItemText primary={seg.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)}>{t("common_cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleUpdateSequence}
            disabled={!editFormValid || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? t("mkt_saving") : t("common_save")}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteStepId}
        onClose={() => setDeleteStepId(null)}
        onConfirm={confirmDeleteStep}
        itemName={t("mkt_step")}
        itemType={t("mkt_step")}
      />
    </Box>
  );
};

export default SequenceDetail;
